
import { Component, Mixins } from 'vue-property-decorator';
import moment from 'moment';
import { PermissionCode } from '@/types';
import { isTaskCompleted, isTaskFailed } from '@/helpers';
import OperationComponentMixin from '@/mixins/OperationComponentMixin.vue';

moment.locale('it');

@Component
export default class OperationCommissioning extends Mixins(OperationComponentMixin) {
  get userPermissions(): PermissionCode[] {
    return this.$store.getters.currentUserPermissions;
  }

  get commissioningStatus(): string | null {
    return this.operation?.commissioningStatus ?? null;
  }

  get connectionLastStep(): string | null {
    return this.operation?.connectionLastStep ?? null;
  }

  get connectionLastStepDate(): string | null {
    return this.operation
      && this.operation.connectionLastStepDate
      && moment(this.operation.connectionLastStepDate).isValid()
      ? moment(this.operation.connectionLastStepDate).format('L')
      : null;
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  // Commissionig (colonna)

  get commissioningNotes(): string | null {
    return this.operationChild?.commissioningNotes ?? null;
  }

  get secondColumnClass(): string {
    // Se il task è completato o in errore, colora ogni colonna rispettivamente di verde o rosso
    if (isTaskCompleted(this.operationChild, 'R')) return 'completed';
    if (isTaskFailed(this.operationChild, 'R')) return 'error';

    // Se l'impianto è collaudato e il portale di monitoraggio è configurato e connesso,
    // la colonna è verde.
    if (this.commissioningStatus === 'tested' && this.inverterConnected) return 'completed';

    return '';
  }

  // Commissioning - Collaudo

  get internetConnectionTested(): boolean {
    return this.operationChild?.internetConnectionTested === true;
  }

  get commissioningDate(): string {
    return this.operation?.commissioningDate
      && moment(this.operation.commissioningDate).isValid()
      ? moment(this.operation.commissioningDate).format('L')
      : '';
  }

  get serviceConfirm(): boolean {
    return this.operationChild?.serviceConfirm === true;
  }

  get internetConnectionColor(): string {
    return this.internetConnectionTested
      ? 'statusDone'
      : 'grey lighten-2';
  }

  get serviceConfirmColor(): string {
    return this.serviceConfirm
      ? 'statusDone'
      : 'grey lighten-2';
  }

  get internetConnectionTooltipColor(): string {
    return this.internetConnectionTested
      ? 'success'
      : 'error';
  }

  get serviceConfirmTooltipColor(): string {
    return this.serviceConfirm
      ? 'success'
      : 'error';
  }

  get internetConnectionTooltipHtml(): string {
    return this.internetConnectionTested
      ? "Connessione ad internet dell'impianto testata."
      : "Connessione ad internet dell'impianto NON testata.";
  }

  get serviceConfirmTooltipHtml(): string {
    return this.serviceConfirm
      ? 'Service SY confermato.'
      : 'Service SY NON confermato.';
  }

  // Commissioning - Portale di monitoraggio

  get inverterConnected(): boolean {
    return this.operationChild?.inverterConnected === true;
  }

  get monitorColor(): string {
    return this.inverterConnected ? 'statusDone' : 'grey lighten-2';
  }

  get monitorTooltipColor(): string {
    return this.inverterConnected ? 'success' : 'error';
  }

  get monitorTooltipText(): string {
    return this.inverterConnected
      ? 'Portale di monitoraggio configurato e connesso'
      : 'Portale di monitoraggio non ancora configurato';
  }

  // Finiture mancanti

  get missingFinishingsStatus(): string | null {
    return this.operationChild?.missingFinishingsStatus ?? null;
  }

  get missingFinishingsNotes(): string | null {
    return this.operationChild?.missingFinishingsNotes ?? null;
  }

  get hasMissingFinishings(): boolean {
    return !!this.missingFinishingsStatus && this.missingFinishingsStatus !== 'na';
  }

  get missingFinishingsColor(): string {
    switch (this.missingFinishingsStatus) {
      case 'todo': return 'orange';
      case 'waiting': return 'orange';
      case 'done': return 'success';
      default: return '';
    }
  }

  get missingFinishingsTooltipText(): string {
    const plain = (this.missingFinishingsNotes || '').replace(/<[^>]*>/g, '').trim();
    return plain || 'Finiture mancanti';
  }

  // Allacciamento (seconda colonna)

  get firstColumnClass(): string {
    // Se il task è completato o in errore, colora ogni colonna rispettivamente di verde o rosso
    if (isTaskCompleted(this.operationChild, 'R')) return 'completed';
    if (isTaskFailed(this.operationChild, 'R')) return 'red';

    // Se il task è in lavorazione e il collaudo è confermato, la colonna è verde.
    if (this.commissioningStatus === 'tested') return 'completed';

    // Se la data è impostata e confermata...
    if (this.commissioningStatus === 'confirmed'
      && this.operation !== null
      && this.operation.commissioningDate !== null) {
      // ... Mostra la colonna gialla se la data è precedente alla data odierna,
      // viceversa arancione.
      const todayDate = moment().startOf('day').toDate();
      const commissioningDate = moment(this.operation.commissioningDate).startOf('day').toDate();

      return moment(todayDate).isSameOrBefore(commissioningDate) ? 'yellow' : 'orange';
    }

    return '';
  }

  get commissioningTooltipColor(): string {
    if (isTaskCompleted(this.operationChild, 'R')) return 'success';
    if (isTaskFailed(this.operationChild, 'R')) return 'error';

    // Commissioning
    switch (this.commissioningStatus) {
      case 'suggested':
        return 'orange';

      case 'confirmed':
      case 'tested':
        return 'success';

      default:
        return 'error';
    }
  }

  get commissioningTooltipText() {
    if (isTaskCompleted(this.operationChild, 'R')) return 'Collaudo confermato';

    // Allacciamento
    switch (this.commissioningStatus) {
      case 'suggested':
        return 'Data proposta';

      case 'confirmed':
        return 'Data confermata';

      case 'tested':
        return 'Collaudo confermato';

      default:
        return 'Data prevista per il collaudo da definire';
    }
  }

  get commissioningClass(): string {
    if (this.connectionLastStep) return '';

    return !this.commissioningStatus
      ? 'grey--text text--lighten-2'
      : '';
  }

  get commissioningText(): string {
    switch (this.commissioningStatus) {
      case 'suggested':
      case 'confirmed':
      case 'tested':
        return `COMMISSIONING<br>${this.commissioningDate}`;

      default:
        return 'da definire';
    }
  }

  // Azioni

  onEdit() {
    console.log('OperationCommissioning: onEdit()'); // eslint-disable-line no-console

    // Se l'utente non ha il permesso di modificare queste informazioni,
    // ignora i clic.
    if (!this.hasPermission('inverterConnected.edit')) return;

    this.$emit('edit');
  }
}
